import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule, DatePipe, NgTemplateOutlet } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { catchError, forkJoin, Observable, of, switchMap } from 'rxjs';

import { AppButtons } from '../../../../shared/app-buttons/app-buttons';
import { PopupModal } from '../../../../shared/popup-modal/popup-modal';
import { DocumentPreviewModal } from '../../../../shared/document-preview-modal/document-preview-modal';
import { AppTooltipComponent } from '../../../../shared/app-tooltip/app-tooltip.component';
import { FileUploadModal } from '../../../../shared/maf/maf-file-upload-card/file-upload-modal/file-upload-modal';
import { EditableReviewTable } from '../../../../shared/editable-review-table/editable-review-table.component';
import {
  ExtractionStatus,
  FileUploadModalConfig,
  getFileUploadDocumentConfig,
} from '../../../../shared/maf/maf-file-upload-card/file-upload-modal/file-upload-modal.config';

import { Images } from '../../../../core/constants/image.constants';
import { ToasterService } from '../../../../shared/toaster/toaster.service';
import { ApplicationReviewService } from '../../../../features/services/application-review.service';
import { ApiService } from '../../../../core/services/api.service';
import { Shared } from '../../../../core/services/shared';
import { DateTimeConversionHelper } from '../../../../shared/helper/date-time-conversion.helper';
import { LeadService } from '../../../../features/services/lead-service';
import { getFromStorage } from '../../../../core/constants/auth-state';
import { AppReviewApiService } from '../../services/app-review-api.service';

import {
  PopupButtonsConfig,
} from '../../../../shared/models/popup-buttons.model';
import {
  ADDITIONAL_DOCUMENT_TYPES,
  DOCUMENT_LABELS,
  DOCUMENT_TOOLTIPS,
  DOCUMENT_TYPE_ALIASES,
  EXTRACTION_COMMON_KEY_ALIASES,
  EXTRACTION_DOC_TYPE_KEY_ALIASES,
  EXTRACTION_FIELD_LABELS,
  EXTRACTION_HIDDEN_KEYS,
  MANDATORY_BASE_DOCUMENT_TYPES,
  MANDATORY_SPONSOR_DOCUMENT_TYPES,
  MINOR_ONLY_DOCUMENT_TYPES,
  OVERRIDE_REASON_OPTIONS,
  POLICY_DOCUMENT_TYPES,
  PRIMARY_DOCUMENT_TYPES_BASE,
  SPONSOR_DOCUMENT_TYPES,
} from '../constants/upload-documents-preview.constants';
import {
  EditableOverrideReasonOption,
  EditableTableColumn,
  EditableTableStatusLabels,
  EditableReviewRow,
} from '../../../../shared/editable-review-table/editable-review-table.model';
import { UploadedDocumentFile } from '../../../../features/models/maf-upload-center.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-upload-documents-preview',
  imports: [
    AppButtons,
    AppTooltipComponent,
    CommonModule,
    DatePipe,
    DocumentPreviewModal,
    EditableReviewTable,
    MatIcon,
    NgTemplateOutlet,
    PopupModal,
  ],
  templateUrl: './upload-documents-preview.html',
  styleUrl: './upload-documents-preview.scss',
})
export class UploadDocumentsPreview implements OnChanges {
  // ───────────────── INPUTS ─────────────────
  @Input() applicantId!: string;
  @Input() applicants: any[] = [];
  @Input() basicInfo: any = null;
  @Input() leadInfo: any = null;
  @Input() contactInfoId = '';
  @Input() mafSubmissions: any[] = [];
  @Input() leadInfoId!: string;
  @Input() mafFinalSubmissionId = '';
  @Input() configurationId = '';
  @Input() reviewQueueId = '';
  @Input() isDocumentReviewed = false;
  @Input() isReadOnly = false;
  @Input() enableCopyMode = false;
  @Input() showActionRequiredBanner = true;
  @Input() isSponsorSelf = false;
  @Output() requestRequote = new EventEmitter<string>();
  @Output() requestRequoteApi = new EventEmitter<string>();
  @Output() backToReviewQueueRequested = new EventEmitter<void>();
  @Output() documentsFinalizedChanged = new EventEmitter<boolean>();
  @Output() reviewStatusRefreshRequested = new EventEmitter<{ message?: string; delayMs?: number } | undefined>();

  isPrimaryApplicant = false;
  applicantDob?: string;

  // ───────────────── STATE ─────────────────
  isOpen = true;
  loading = false;
  error: string | null = null;

  showDeleteModal = false;
  showPreviewModal = false;
  showPassportDeleteModal = false;
  showRequoteWarningModal = false;
  isAllMarkedFinal = false;

  deleteModalTitle = '';
  deleteModalSections: Array<{
    icon?: string;
    iconClass?: string;
    label?: string;
    text: string;
  }> = [];

  previewFile: any = null;
  requoteWarningRows: Array<{
    documentId: string;
    rowKey: string;
    fieldLabel: string;
    submittedValue: string;
    extractedValue: string;
    finalValue: string;
  }> = [];
  private lastRequoteWarningRows: Array<{
    documentId: string;
    rowKey: string;
    fieldLabel: string;
    submittedValue: string;
    extractedValue: string;
    finalValue: string;
  }> = [];
  expandedRows = new Set<string>();
  deletingDocumentIds = new Set<string>();
  docFlashStates = new Map<string, 'match' | 'mismatch' | 'corrected' | 'deleted'>();
  private readonly docFlashTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly missingOverrideReasonRows = new Map<string, Set<string>>();
  private readonly missingFinalValueRows = new Map<string, Set<string>>();
  private readonly missingMandatoryDocTypes = new Set<string>();
  private readonly acceptedMismatchRowKeysByDocument = new Map<string, Set<string>>();

  private pendingDeleteFile: any = null;
  private hasPendingEdits = false;
  private applicantDocumentsSnapshot: any[] = [];
  private sponsorshipDocumentsSnapshot: any[] = [];
  private documentExtractedFieldsSnapshot: any[] = [];
  private mafFormSubmissionsSnapshot: any[] = [];

  documents: any[] = [];
  sponsorshipDocuments: any[] = [];
  private readonly destroyRef = inject(DestroyRef);
  private readonly shared = inject(Shared);
  private readonly leadService = inject(LeadService);
  private readonly appReviewApiService = inject(AppReviewApiService, { optional: true });

  readonly images = Images;
  readonly extractedTableHintText =
    'Verify and confirm the Final Value. Click the field to edit.';
  readonly finalValueEditTooltipText = 'Click to edit';
  readonly tableStatusLabels: EditableTableStatusLabels = {
    match: 'Match',
    mismatch: 'Mismatch',
    corrected: 'Corrected',
    accepted: 'Accepted',
  };
  readonly overrideReasonOptions: EditableOverrideReasonOption[] =
    OVERRIDE_REASON_OPTIONS;
  readonly extractedFieldTableColumns: EditableTableColumn[] = [
    { key: 'label', header: 'Profile Field', type: 'text', showIndex: true },
    { key: 'submittedValue', header: 'Submitted Value', type: 'text' },
    { key: 'extractedValue', header: 'Extracted Value', type: 'text' },
    { key: 'confidence', header: 'Confidence', type: 'percent' },
    { key: 'status', header: 'Status', type: 'status' },
    {
      key: 'finalValue',
      header: 'Final Value',
      headerMetaText: '(Editable)',
      type: 'input',
      clickableToEdit: true,
      placeholder: 'Enter final value',
    },
    {
      key: 'overrideReason',
      header: 'Override Reason',
      headerTooltip: this.getOverrideReasonTooltipText(),
      type: 'select',
      options: OVERRIDE_REASON_OPTIONS,
      placeholder: 'Choose one',
    },
  ];

  // Maintains editable table values per document and field key.
  private readonly extractedFieldEdits = new Map<
    string,
    Map<string, { finalValue: string; overrideReason: string; isEdited: boolean }>
  >();

  // ───────────────── CONSTANTS ─────────────────

  readonly primaryDocTypesBase = [...PRIMARY_DOCUMENT_TYPES_BASE];
  readonly policyDocTypes = [...POLICY_DOCUMENT_TYPES];
  readonly additionalDocTypes = [...ADDITIONAL_DOCUMENT_TYPES];
  readonly sponsorDocTypes = [...SPONSOR_DOCUMENT_TYPES];
  readonly mandatoryBaseDocTypes = new Set(MANDATORY_BASE_DOCUMENT_TYPES);
  readonly mandatorySponsorDocTypes = new Set(MANDATORY_SPONSOR_DOCUMENT_TYPES);

  deleteConfirmLabel = 'Confirm Delete';
  deleteModalButtons: PopupButtonsConfig[] = [
    {
      layout: 'stacked',
      buttons: [
        {
          label: 'Cancel',
          variant: 'secondary',
          action: () => this.cancelDelete(),
        },
        {
          label: this.deleteConfirmLabel,
          variant: 'primary',
          action: () => this.confirmDelete(),
        },
      ],
    },
  ];

  readonly requoteWarningButtons: PopupButtonsConfig[] = [
    {
      layout: 'stacked',
      buttons: [
        {
          label: 'Request Requote',
          variant: 'primary',
          action: () => this.onRequestRequoteFromWarning(),
        },
        {
          label: 'Ignore & Accept',
          variant: 'tertiary',
          action: () => this.onIgnoreAndAcceptFromRequoteWarning(),
        },
        {
          label: 'Back to Review',
          variant: 'tertiary',
          action: () => this.onBackToReviewFromRequoteWarning(),
        },
      ],
    },
  ];

  private readonly docTooltips: Record<string, string> = DOCUMENT_TOOLTIPS;
  // ───────────────── CONSTRUCTOR ─────────────────
  constructor(
    private readonly toaster: ToasterService,
    private readonly applicationReviewService: ApplicationReviewService,
    private readonly apiService: ApiService,
    private readonly dialog: MatDialog,
  ) {
    this.destroyRef.onDestroy(() => {
      this.docFlashTimers.forEach((timer) => clearTimeout(timer));
      this.docFlashTimers.clear();
    });
  }

  // ───────────────── LIFECYCLE ─────────────────
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isDocumentReviewed']) {
      this.isAllMarkedFinal = this.resolveFinalizedState();
      this.documentsFinalizedChanged.emit(this.isAllMarkedFinal);
    }

    if (changes['applicantId']?.currentValue || changes['leadInfoId']?.currentValue) {
      this.fetchDocuments();
    }
  }

  get shouldShowActionRequiredBanner(): boolean {
    return this.showActionRequiredBanner && this.hasActionRequiredIssues();
  }

  // ───────────────── GETTERS ─────────────────
  get primaryDocTypes(): string[] {
    if (!this.isPrimaryApplicant || !this.applicantDob) {
      return this.filterMinorDocs(this.primaryDocTypesBase, false);
    }

    const age = this.getAge();
    return this.filterMinorDocs(
      this.primaryDocTypesBase,
      age !== null && age <= 6,
    );
  }

  get isSponsorCompany(): boolean {
    const sponsor = this.sponsorshipDocuments?.[0]?.sponsor;
    return (
      !!sponsor &&
      (String(sponsor).toLowerCase().includes('company') || sponsor === '2')
    );
  }

  get extractedFieldTableColumnsByMode(): EditableTableColumn[] {
    if (!this.isInteractionReadOnly) {
      return this.extractedFieldTableColumns;
    }

    return this.extractedFieldTableColumns.map((column) => {
      if (column.key === 'finalValue') {
        return {
          ...column,
          type: 'text',
          clickableToEdit: false,
          headerMetaText: undefined,
        };
      }

      return column;
    });
  }

  // ───────────────── PUBLIC METHODS ─────────────────
  toggleSection(): void {
    this.isOpen = !this.isOpen;
  }

  isRowExpanded(file: any): boolean {
    return !!file?.documentId && this.expandedRows.has(file.documentId);
  }

  isPassportDocument(docType: string): boolean {
    return this.normalizeDocType(docType) === 'Passport';
  }

  canShowExtractionTable(file: any, docType: string): boolean {
    if (!file) {
      return false;
    }

    const normalizedType = this.normalizeDocType(docType);
    if (!this.isDocExtractable(normalizedType)) {
      return false;
    }

    if (!this.isPassportDocument(normalizedType)) {
      return true;
    }

    return this.getFieldRows(file, normalizedType).length > 0;
  }

  isRowDeleting(file: any): boolean {
    return !!file?.documentId && this.deletingDocumentIds.has(file.documentId);
  }

  parseDate(dateStr: string): Date | null {
    if (!dateStr) return null;

    const parsed = new Date(dateStr);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }

    const [day, month, year] = dateStr.split('-');
    return new Date(+year, +month - 1, +day);
  }

  getDocLabel(type: string): string {
    const normalizedType = this.normalizeDocType(type);
    return DOCUMENT_LABELS[normalizedType] ?? getFileUploadDocumentConfig(normalizedType)?.label ?? normalizedType;
  }

  getDocTooltip(docType: string): string | null {
    const normalizedType = this.normalizeDocType(docType);
    return this.docTooltips?.[normalizedType] ?? null;
  }

  shouldShowDocumentInfoTooltip(file: any, docType: string): boolean {
    if (!this.getDocTooltip(docType)) {
      return false;
    }

    return this.getRowStatusIcon(file, docType) === 'none';
  }

  isMandatoryDocType(docType: string, isSponsorSection = false): boolean {
    const normalized = this.normalizeDocType(docType);
    return isSponsorSection
      ? this.mandatorySponsorDocTypes.has(normalized)
      : this.mandatoryBaseDocTypes.has(normalized);
  }

  getDocDisplayName(file: any, docType: string, isSponsorSection = false): string {
    const baseName = file?.fileName || this.getDocLabel(docType);
    if (this.isMandatoryDocType(docType, isSponsorSection)) {
      return `* ${baseName}`;
    }

    return baseName;
  }

  isDocExtractable(docType: string): boolean {
    return !!getFileUploadDocumentConfig(this.normalizeDocType(docType))?.isExtractable;
  }

  getFieldRows(file: any, docType: string): EditableReviewRow[] {
    if (!file) {
      return [];
    }

    const normalizedDocType = this.normalizeDocType(docType);
    if (normalizedDocType !== 'Passport') {
      return this.getDocumentFieldRows(file, docType);
    }

    const passportFiles = this.documents.filter((doc) => this.normalizeDocType(String(doc?.documentType ?? '')) === 'Passport');
    if (passportFiles.length <= 1) {
      return this.getDocumentFieldRows(file, docType);
    }

    const ownRows = this.getDocumentFieldRows(file, docType);
    if (ownRows.length > 0) {
      return this.getCombinedPassportRows();
    }

    return [];
  }

  private getDocumentFieldRows(file: any, docType: string): EditableReviewRow[] {
    if (!file) {
      return [];
    }

    const rawRows = Array.isArray(file?.extractedFields)
      ? file.extractedFields
      : [];
    const acceptedKeys = this.acceptedMismatchRowKeysByDocument.get(String(file?.documentId ?? '')) ?? new Set<string>();
    const rowsFromPayload = rawRows.map((row: any, index: number) => {
      const key = String(row?.key ?? row?.fieldKey ?? `field-${index + 1}`);
      const label = String(
        row?.label ?? row?.fieldName ?? `Field ${index + 1}`,
      );
      const submittedValue = String(
        row?.submittedValue ?? row?.submitted ?? '-',
      );
      const extractedValue = String(
        row?.extractedValue ?? row?.extracted ?? '-',
      );
      const confidence = this.normalizeConfidence(row?.confidence);
      const apiFinalValue = String(row?.finalValue ?? extractedValue ?? '-');
      const apiOverrideReason = String(row?.overrideReason ?? '');
      const existingState = this.getExistingFieldEditState(file?.documentId, key);
      const finalValue = existingState?.finalValue ?? apiFinalValue;
      const overrideReason = existingState?.overrideReason ?? apiOverrideReason;
      const isAcceptedMismatch = acceptedKeys.has(key);

      return {
        key,
        label,
        submittedValue,
        extractedValue,
        confidence,
        finalValue,
        isFinalValueEdited: existingState?.isEdited === true,
        status: isAcceptedMismatch
          ? 'accepted'
          : this.resolveRowStatus(
            row?.status,
            existingState?.isEdited === true,
            finalValue,
            overrideReason,
          ),
        overrideReason,
      };
    });

    if (rowsFromPayload.length > 0) {
      return rowsFromPayload;
    }
    return [];
  }

  private getCombinedPassportRows(): EditableReviewRow[] {
    const passportFiles = this.documents.filter((doc) => this.normalizeDocType(String(doc?.documentType ?? '')) === 'Passport');
    if (passportFiles.length <= 1) {
      return passportFiles.flatMap((file) => this.getDocumentFieldRows(file, file?.documentType ?? 'Passport'));
    }

    const merged = new Map<string, EditableReviewRow>();
    passportFiles.forEach((file) => {
      this.getDocumentFieldRows(file, file?.documentType ?? 'Passport').forEach((row) => {
        const existing = merged.get(row.key);
        if (!existing) {
          merged.set(row.key, row);
          return;
        }

        const choosePreferred = (primary: EditableReviewRow, candidate: EditableReviewRow): EditableReviewRow => {
          if ((primary.finalValue ?? '').trim() && (primary.finalValue ?? '').trim() !== '-') {
            return primary;
          }

          if ((candidate.finalValue ?? '').trim() && (candidate.finalValue ?? '').trim() !== '-') {
            return candidate;
          }

          if ((primary.submittedValue ?? '').trim() && (primary.submittedValue ?? '').trim() !== '-') {
            return primary;
          }

          return candidate;
        };

        merged.set(row.key, choosePreferred(existing, row));
      });
    });

    return Array.from(merged.values());
  }

  getDocumentBannerType(
    file: any,
    docType: string,
  ): 'match' | 'mismatch' | 'failed' | 'none' {
    if (!this.isDocExtractable(docType)) {
      return 'none';
    }

    const statusCode = this.getExtractionStatusCode(file?.extractionStatus);
    if (statusCode === ExtractionStatus.NotExtracted) {
      return 'none';
    }

    if (statusCode === ExtractionStatus.ExtractionFailed) {
      return 'failed';
    }

    if (statusCode === ExtractionStatus.Extracted || statusCode === ExtractionStatus.Overridden) {
      const rows = this.getFieldRows(file, docType);
      const mismatchCount = rows.filter(
        (row) => row.status === 'mismatch',
      ).length;
      return mismatchCount > 0 ? 'mismatch' : 'match';
    }

    return 'none';
  }

  getDocumentMismatchCount(file: any, docType: string): number {
    if (this.getExtractionStatusCode(file?.extractionStatus) === ExtractionStatus.NotExtracted) {
      return 0;
    }

    const payloadCount = Number(file?.mismatchCount);
    if (Number.isFinite(payloadCount) && payloadCount > 0) {
      return payloadCount;
    }

    return this.getFieldRows(file, docType).filter(
      (row) => row.status === 'mismatch',
    ).length;
  }

  getDocumentFailureTooltip(file: any): string {
    return String(
      file?.extractionFailureReason ||
      'Extraction Failed. Please check the quality / document type.',
    );
  }

  getMissingOverrideReasonRowKeys(file: any): string[] {
    if (!file?.documentId) {
      return [];
    }

    return Array.from(this.missingOverrideReasonRows.get(file.documentId) ?? []);
  }

  getMissingFinalValueRowKeys(file: any): string[] {
    if (!file?.documentId) {
      return [];
    }

    // Only return keys that were flagged during validation AND still have missing values
    const flaggedKeys = this.missingFinalValueRows.get(file.documentId) ?? new Set<string>();
    const rows = this.getFieldRows(file, file?.documentType || '');

    return Array.from(flaggedKeys).filter((key) => {

      if (this.isFinalValueOptional(file.documentType, key)) {
        return false;
      }

      const row = rows.find((r) => r.key === key);
      if (!row) return false;
      // Check if value is still actually missing
      const finalValue = String(row.finalValue ?? '').trim();
      return !finalValue || finalValue === '-';
    });
  }

  isMandatoryDocTypeMissing(docType: string): boolean {
    const normalizedType = this.normalizeDocType(docType);
    // Only show as missing if it was flagged AND still has no uploaded documents
    return this.missingMandatoryDocTypes.has(normalizedType) && this.getDocumentsByType(normalizedType).length === 0;
  }

  onMarkAllAsFinal(): void {
    if (this.isReadOnly) {
      return;
    }

    if (this.isAllMarkedFinal) {
      this.persistDocumentChanges('mark-final', false);
      return;
    }

    this.executeMarkFinalFlow();
  }

  saveAllDocumentChanges(): void {
    if (this.isInteractionReadOnly) {
      return;
    }

    this.missingOverrideReasonRows.clear();
    this.missingFinalValueRows.clear();
    this.missingMandatoryDocTypes.clear();

    const overrideReasonValidationMessage = this.validateEditedRowsBeforeSave();
    if (overrideReasonValidationMessage) {
      this.showToast(overrideReasonValidationMessage, 'error');
      return;
    }

    this.persistDocumentChanges('save');
  }

  getAdditionalDocumentsSectionNumber(): number {
    return this.isPrimaryApplicant && this.isSponsorCompany && !this.isLeadSelfSponsored() ? 4 : 3;
  }

  private executeMarkFinalFlow(skipPotentialMismatchWarning = false): void {

    this.missingOverrideReasonRows.clear();
    this.missingFinalValueRows.clear();
    this.missingMandatoryDocTypes.clear();

    if (!this.validateMandatoryDocumentsBeforeMarkFinal()) {
      this.scrollToFirstErrorDocument();
      return;
    }

    if (!skipPotentialMismatchWarning) {
      const requoteFields = this.getRequoteMismatchRows();
      if (requoteFields.length > 0) {
        this.requoteWarningRows = requoteFields;
        this.lastRequoteWarningRows = [...requoteFields];
        this.showRequoteWarningModal = true;
        return;
      }
    }

    const readinessMessage = this.validateExtractedRowsForSave();
    if (readinessMessage) {
      this.showToast(readinessMessage, 'warning');
      this.scrollToFirstErrorDocument();
      return;
    }

    const validationMessage = this.validateRowsBeforeMarkFinal();
    if (validationMessage) {
      this.showToast(validationMessage, 'warning');
      this.scrollToFirstErrorDocument();
      return;
    }

    this.persistDocumentChanges('mark-final', true);
  }

  isSectionReady(types: string[], isSponsorSection = false): boolean {
    const isUploaded = isSponsorSection
      ? this.getSponsorUploadedCount(types) === types.length
      : this.getUploadedCount(types) === types.length;

    if (!isUploaded) {
      return false;
    }

    const docs = isSponsorSection
      ? this.sponsorshipDocuments.flatMap((s: any) => s.companyDocuments ?? [])
      : this.documents;
    const extractableDocs = docs.filter((doc: any) =>
      types.includes(doc.documentType) && this.isDocExtractable(doc.documentType),
    );

    return extractableDocs.every((doc: any) => !!doc.isFinalized);
  }

  onFinalValueChanged(file: any, rowKey: string, value: string): void {
    if (this.isInteractionReadOnly) {
      return;
    }

    const rows = this.getFieldRows(file, file?.documentType || '');
    const row = rows.find((r) => r.key === rowKey);
    const edit = this.getFieldEditState(
      file?.documentId,
      rowKey,
      value || '-',
      String(row?.overrideReason ?? ''),
    );
    edit.finalValue = value || '-';
    edit.isEdited = true;

    if (row) {
      const finalValue = String(edit.finalValue ?? '').trim();
      if (!finalValue || finalValue === '-') {
        edit.overrideReason = '';
      }

      const missingKeys = this.missingOverrideReasonRows.get(file?.documentId);
      if (missingKeys?.has(rowKey) && edit.overrideReason.trim()) {
        missingKeys.delete(rowKey);
        if (missingKeys.size === 0) {
          this.missingOverrideReasonRows.delete(file?.documentId);
        }
      }
    }

    this.triggerDocumentFlashFromCurrentState(file);
    this.hasPendingEdits = true;
    this.syncTouchedState(true);
  }

  onOverrideReasonChanged(file: any, rowKey: string, reason: string): void {
    if (this.isInteractionReadOnly) {
      return;
    }

    const rows = this.getFieldRows(file, file?.documentType || '');
    const row = rows.find((r) => r.key === rowKey);
    const edit = this.getFieldEditState(
      file?.documentId,
      rowKey,
      String(row?.finalValue ?? '-'),
      String(row?.overrideReason ?? ''),
    );

    if (row) {
      const finalValue = String(edit.finalValue ?? '').trim();
      if (!finalValue || finalValue === '-') {
        edit.overrideReason = '';
        return;
      }
    }
    edit.overrideReason = reason;

    const missingKeys = this.missingOverrideReasonRows.get(file?.documentId);
    if (missingKeys?.has(rowKey) && reason.trim()) {
      missingKeys.delete(rowKey);
      if (missingKeys.size === 0) {
        this.missingOverrideReasonRows.delete(file?.documentId);
      }
    }

    this.triggerDocumentFlashFromCurrentState(file);
    this.hasPendingEdits = true;
    this.syncTouchedState(true);
  }

  hasUnsavedDocumentChanges(): boolean {
    return this.hasPendingEdits;
  }

  getDocumentFlashState(file: any): 'match' | 'mismatch' | 'corrected' | 'deleted' | null {
    if (!file?.documentId) {
      return null;
    }

    return this.docFlashStates.get(file.documentId) ?? null;
  }

  shouldHighlightRow(file: any, rowKey: string, columnKey: string): boolean {
    if (!file?.documentId) {
      return false;
    }

    // Only highlight final value cells, not override reason cells
    if (columnKey !== 'finalValue') {
      return false;
    }

    return this.missingOverrideReasonRows.get(file.documentId)?.has(rowKey) ?? false;
  }

  shouldHighlightDocumentRow(file: any): boolean {
    if (!file?.documentId) {
      return false;
    }

    // Show error if either missing override reason OR missing final value
    const hasMissingOverrideReason = (this.missingOverrideReasonRows.get(file.documentId)?.size ?? 0) > 0;
    const hasMissingFinalValue = (this.missingFinalValueRows.get(file.documentId)?.size ?? 0) > 0;
    return hasMissingOverrideReason || hasMissingFinalValue;
  }

  scrollToFirstErrorDocument(): void {
    setTimeout(() => {
      const errorElement = document.querySelector('.doc-collector__row--error');
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }

  getRowStatusIcon(file: any, docType: string): 'success' | 'info' | 'warning' | 'none' {
    if (!file) {
      return 'none';
    }
    if (!this.isDocExtractable(docType)) {
      return 'success';
    }

    if (this.getExtractionStatusCode(file?.extractionStatus) === ExtractionStatus.NotExtracted) {
      return 'none';
    }

    const rows = this.getFieldRows(file, docType);
    if (rows.some((row) => row.status === 'mismatch')) {
      return 'warning';
    }

    if (rows.some((row) => row.status === 'corrected' || row.status === 'accepted')) {
      return 'info';
    }

    return 'success';
  }

  fetchDocuments(): void {
    if (!this.leadInfoId) return;

    this.loading = true;
    this.error = null;

    const payload = {
      leadInfoId: this.leadInfoId,
      applicants: this.applicants || [],
      basicInfo: this.basicInfo || null,
    };

    this.applicationReviewService
      .getApplicationReviewDocuments(this.leadInfoId, payload)
      .subscribe({
        next: (res: any) => this.handleDocumentResponse(res),
        error: () => this.handleError(),
      });
  }

  getDocumentsByType(type: string): any[] {
    const normalizedType = this.normalizeDocType(type);
    return this.documents.filter((d) => this.normalizeDocType(String(d?.documentType ?? '')) === normalizedType);
  }

  getUploadedCount(types: string[]): number {
    return types.filter((t) => this.getDocumentsByType(t).length).length;
  }

  getSponsorDocument(
    sponsor: { companyDocuments?: Array<{ documentType: string }> },
    docType: string
  ): { documentType: string } | null {
    const normalizedType = this.normalizeDocType(docType);
    return (
      sponsor?.companyDocuments?.find((d) =>
        this.normalizeDocType(String(d?.documentType ?? '')) === normalizedType,
      ) ??
      null
    );
  }
  getSponsorUploadedCount(docTypes: string[]): number {
    const allDocs = this.sponsorshipDocuments.flatMap(
      (s: any) => s.companyDocuments ?? [],
    );

    return docTypes.filter((type) => {
      const normalizedType = this.normalizeDocType(type);
      return allDocs.some((d: any) =>
        this.normalizeDocType(String(d?.documentType ?? '')) === normalizedType,
      );
    },
    ).length;
  }

  get availableUploadDocTypes(): string[] {
    const applicantDocTypes = [
      ...this.primaryDocTypes,
      ...this.policyDocTypes,
      ...this.additionalDocTypes,
    ];

    const sponsorDocTypes =
      this.isPrimaryApplicant && this.isSponsorCompany && !this.isLeadSelfSponsored() ? this.sponsorDocTypes : [];

    return Array.from(new Set([...applicantDocTypes, ...sponsorDocTypes]));
  }

  handleFileAction(action: 'view' | 'download', file: any): void {
    if (action === 'view') {
      this.previewFile = file;
      this.showPreviewModal = true;
      return;
    }

    window.open(file.downloadFilePath, '_blank');
    this.showToast(`${file.fileName} download started.`);
  }

  onDownloadAll(): void {
    const stitched = this.appReviewApiService?.currentStitchedSummarySignal?.();
    const finalSubmissionId = (
      this.mafFinalSubmissionId ||
      stitched?.mafInfo?.mafFinalSubmissionId ||
      stitched?.maf?.mafFinalSubmissionId ||
      ''
    )?.trim();
    if (!finalSubmissionId) {
      this.showToast('MAF final submission ID is missing. Unable to download ZIP.', 'error');
      return;
    }

    const leadInfoId = (
      this.leadInfoId ||
      this.leadInfo?.id ||
      this.leadInfo?.leadInfoId ||
      stitched?.leadInfo?.id ||
      stitched?.leadInfoId ||
      ''
    )?.trim();
    if (!leadInfoId) {
      this.showToast('Lead ID is missing. Unable to download ZIP.', 'error');
      return;
    }

    const configId = this.resolveConfigurationId();

    this.apiService.subscribeWithCleanup(
      this.leadService.getLeadDocuments({
        leadId: leadInfoId,
        userType: 'Agent',
      }).pipe(
        switchMap((res) => {
          const raw = (res as any)?.body?.leadDocuments ?? (res as any)?.body ?? res;
          const leadDocument = {
            status: raw?.status ?? 'Active',
            modifiedOn: raw?.modifiedOn ?? new Date().toISOString(),
            leadName: raw?.leadName ?? null,
            applicantsDocuments: Array.isArray(raw?.applicantsDocuments) ? raw.applicantsDocuments : [],
            companyDocuments: Array.isArray(raw?.companyDocuments) ? raw.companyDocuments : [],
            ...(raw && typeof raw === 'object' ? raw : {}),
          };

          const payload: {
            mafFinalSubmissionId: string;
            configurationID: string;
            selectedApplicantId?: string;
            leadDocument: any;
          } = {
            mafFinalSubmissionId: finalSubmissionId,
            configurationID: configId,
            leadDocument,
          };

          if (this.applicantId?.trim()) {
            payload.selectedApplicantId = this.applicantId.trim();
          }

          return this.apiService.downloadAllMafZips(payload);
        }),
        catchError(() => {
          this.showToast('Failed to download all documents ZIP.', 'error');
          return of(null);
        }),
      ),
      (response) => {
        if (!response) {
          return;
        }

        const fileBlob = response.body;
        if (!fileBlob) {
          this.showToast('No file received for download.', 'error');
          return;
        }

        const fileName = this.getDownloadFileName(
          response.headers.get('content-disposition'),
        );
        this.triggerBrowserDownload(fileBlob, fileName);
        this.showToast('All documents ZIP download started.', 'success');
      },
      this.destroyRef,
    );
  }

  private resolveConfigurationId(): string {
    const fromInput = this.configurationId?.trim();
    if (fromInput) {
      return fromInput;
    }
    const fromSubmissions = this.mafSubmissions?.find(
      (s: any) => s?.mafConfigurationId || s?.configurationId,
    );
    const submissionConfigId = (fromSubmissions?.mafConfigurationId || fromSubmissions?.configurationId || '')?.trim();
    if (submissionConfigId) {
      return submissionConfigId;
    }
    const stitched = this.appReviewApiService?.currentStitchedSummarySignal?.();
    const stitchedConfigId = (
      stitched?.mafInfo?.mafUploadedDocuments?.mafConfigurationId ||
      stitched?.mafInfo?.mafConfigurationId ||
      stitched?.maf?.mafConfigurationId ||
      stitched?.headerSummary?.mafConfigurationId ||
      ''
    )?.trim();
    if (stitchedConfigId) {
      return stitchedConfigId;
    }
    const fromLead = (this.leadInfo?.mafConfigurationId || this.leadInfo?.configurationId || '')?.trim();
    if (fromLead) {
      return fromLead;
    }
    const fromStorage = getFromStorage('configurationId');
    if (fromStorage && typeof fromStorage === 'string') {
      return fromStorage.trim();
    }
    return '';
  }

  closePreviewModal(): void {
    this.showPreviewModal = false;
    this.previewFile = null;
  }
  toggleExpandRow(file: any): void {
    if (!file?.documentId) return;

    this.expandedRows.has(file.documentId)
      ? this.expandedRows.delete(file.documentId)
      : this.expandedRows.add(file.documentId);
  }

  openUploadModal(): void {
    if (this.isInteractionReadOnly) {
      return;
    }

    const dialogRef = this.dialog.open(FileUploadModal, {
      data: this.buildGlobalUploadConfig(),
      width: '500px',
      panelClass: 'custom-upload-modal-panel',
    });

    dialogRef.afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((files: UploadedDocumentFile[]) => {
        if (!files?.length) {
          return;
        }

        const selectedDocType = String(files[0]?.documentType ?? '');
        if (!selectedDocType) {
          return;
        }

        this.onFilesUploaded(selectedDocType, files);
      });
  }

  triggerUpload(docType: string): void {
    if (this.isInteractionReadOnly) {
      return;
    }

    const dialogRef = this.dialog.open(FileUploadModal, {
      data: this.buildUploadConfig(docType),
      width: '500px',
      panelClass: 'custom-upload-modal-panel',
    });

    dialogRef.afterClosed().subscribe((files: UploadedDocumentFile[]) => {
      if (files?.length) this.onFilesUploaded(docType, files);
    });
  }

  onFilesUploaded(docType: string, files: UploadedDocumentFile[]): void {
    if (this.isInteractionReadOnly) {
      return;
    }

    const now = new Date();
    const uploadedRows = files.map((uploadedFile, index) => ({
      documentId: `${docType}-${now.getTime()}-${index}`,
      fileName: uploadedFile.name || `uploaded-${index + 1}`,
      documentType: docType,
      date: now.toISOString(),
      fileSize: uploadedFile.size || 0,
      source: this.getDocLabel(docType),
      extractionStatus: this.isDocExtractable(docType) ? 'Extracted' : 'Uploaded',
    }));

    if (docType === 'Passport') {
      this.documents = this.documents.filter((d) => d.documentType !== 'Passport');
    }

    this.documents = [...this.documents, ...uploadedRows];
    this.showToast(`${uploadedRows.length} file(s) uploaded for ${this.getDocLabel(docType)}.`, 'success');
  }
  // ───────────────── DELETE ─────────────────
  onDeleteFile(file: any): void {
    if (this.isInteractionReadOnly) {
      return;
    }

    this.pendingDeleteFile = file;

    if (file?.documentType === 'Passport') {
      this.deleteConfirmLabel = 'Confirm & Delete Both';
      this.deleteModalButtons[0].buttons[1].label = this.deleteConfirmLabel;
      this.deleteModalTitle = 'Remove Passport Files?';
      this.deleteModalSections = [
        {
          icon: 'info-icon',
          iconClass: 'danger',
          label: 'File Deletion:',
          text: 'Both pages (front & back) will be deleted from the system.',
        },
        {
          icon: 'info-icon',
          iconClass: 'danger',
          label: 'Data Loss:',
          text: 'All extracted fields sourced from this passport will be erased.',
        },
        {
          icon: 'icon-info-circle',
          iconClass: 'info',
          label: 'Client Update:',
          text: 'A notification of the document removal will be sent to the client.',
        },
        { text: 'Are you sure you want to proceed?' },
      ];
      this.showDeleteModal = true;
    } else {
      this.deleteConfirmLabel = 'Confirm Delete';
      this.deleteModalButtons[0].buttons[1].label = this.deleteConfirmLabel;
      this.deleteModalTitle = `Remove the ${this.getDocLabel(file.documentType)}?`;
      this.deleteModalSections = [
        { label: 'Impact on Extracted Data:', text: '' },
        {
          text: 'Deleting this document will clear all associated data fields in the Extracted Fields Review section.',
        },
        {
          text: 'You will need to upload a new source to re-verify this information.',
        },
      ];
      this.showDeleteModal = true;
    }
  }

  confirmDelete(): void {
    if (this.isInteractionReadOnly) {
      return;
    }

    if (!this.pendingDeleteFile?.documentId) return;

    const files =
      this.pendingDeleteFile.documentType === 'Passport'
        ? this.documents.filter((d) => d.documentType === 'Passport')
        : [this.pendingDeleteFile];

    files.forEach((file) => {
      if (file?.documentId) {
        this.deletingDocumentIds.add(file.documentId);
        this.triggerDocumentFlash(file, 'deleted');
      }
    });

    this.showDeleteModal = false;

    this.apiService.subscribeWithCleanup(
      forkJoin(
        files.map((file) =>
          this.deleteFile(file).pipe(catchError(() => of(null))),
        ),
      ),
      (responses) => {
        const deletedCount = responses.filter((response) => response !== null).length;
        this.pendingDeleteFile = null;
        this.deletingDocumentIds.clear();

        if (deletedCount === 0) {
          this.showToast('Failed to delete document(s). Please try again.', 'error');
          return;
        }

        this.fetchDocuments();

        if (deletedCount < files.length) {
          this.showToast('Some documents were deleted. Please retry failed deletions.', 'error');
          return;
        }

        if (files[0]?.documentType === 'Passport') {
          this.showToast('Passport files and extracted data have been cleared. Please upload a new passport.', 'success');
          return;
        }

        this.showToast(`${this.getDocLabel(files[0]?.documentType)} and extracted data have been cleared. Please upload a new document to proceed.`, 'success');
      },
      this.destroyRef,
    );
  }

  cancelDelete(): void {
    this.pendingDeleteFile = null;
    this.showDeleteModal = false;
  }

  // ───────────────── PRIVATE METHODS ─────────────────
  private handleDocumentResponse(response: any): void {
    const body = response?.body ?? response ?? {};
    this.applicantDocumentsSnapshot =
      Array.isArray(body?.documents?.applicantsDocuments)
        ? body.documents.applicantsDocuments
        : [];
    this.sponsorshipDocumentsSnapshot =
      Array.isArray(body?.documents?.sponsorshipsDocument)
        ? body.documents.sponsorshipsDocument
        : [];
    this.documentExtractedFieldsSnapshot =
      Array.isArray(body?.documentExtractedFields)
        ? body.documentExtractedFields
        : [];
    this.mafFormSubmissionsSnapshot =
      Array.isArray(body?.documents?.mafFormSubmissions)
        ? body.documents.mafFormSubmissions
        : [];

    // Find applicant by applicantId
    const applicant = this.applicantDocumentsSnapshot.find(
      (a: any) => a.applicantId === this.applicantId,
      // (a: any) => a.applicantId === 'd1ab9033-6c76-40fa-a973-cf26bc39d994',
    );

    // Set isPrimaryApplicant and applicantDob from response
    this.isPrimaryApplicant = !!applicant?.isPrimaryApplicant;
    this.applicantDob = applicant?.dob ?? undefined;

    // Map applicant documents
    this.documents = (applicant?.documents ?? []).map((d: any) => ({
      ...d,
      documentType: this.normalizeDocType(d.documentType),
    }));

    // Map sponsorship documents
    this.sponsorshipDocuments = this.isPrimaryApplicant
      ? this.sponsorshipDocumentsSnapshot.map((s: any) => ({
        ...s,
        companyDocuments: (s.companyDocuments ?? []).map((d: any) => ({
          ...d,
          documentType: this.normalizeDocType(d.documentType),
        })),
      }))
      : [];

    this.reconcileSponsorDocumentsFromApplicant();

    // Attach extracted fields from documentExtractedFields
    if (this.documentExtractedFieldsSnapshot.length > 0) {
      for (const docField of this.documentExtractedFieldsSnapshot) {
        this.attachExtractedFieldToDocumentCollection(this.documents, docField);
        this.sponsorshipDocuments.forEach((sponsorship: any) => {
          this.attachExtractedFieldToDocumentCollection(sponsorship?.companyDocuments ?? [], docField);
        });
      }
    }

    this.isAllMarkedFinal = this.resolveFinalizedState();
    this.documentsFinalizedChanged.emit(this.isAllMarkedFinal);

    this.loading = false;
  }

  private handleError(): void {
    this.error = 'Failed to load documents.';
    this.loading = false;
  }

  // Removed loadMockScenarioData. All data now comes from API.

  private isDateField(docType: string, fieldKey: string): boolean {
    const dateFieldPatterns = ['dateofbirth', 'dob', 'expirydate', 'dateofexpiry', 'dateofdeath'];
    const normalizedKey = fieldKey.toLowerCase().replace(/[^a-z0-9]/g, '');
    return dateFieldPatterns.some(pattern => normalizedKey.includes(pattern));
  }

  /**
   * Parse extracted fields from documentExtractedFields API response.
   */
  private parseExtractedFields(docField: any): any[] {
    const fields: any[] = [];
    const docType = String(docField?.documentType ?? '');
    const submitted = this.safeParseExtractionJson(docField?.submittedValueJson);
    const extracted = this.safeParseExtractionJson(docField?.extractedValueJson);
    const finalVal = this.safeParseExtractionJson(docField?.finalValueJson);
    const fieldStatus = this.safeParseExtractionJson(docField?.fieldStatusJson);
    const confidenceScores = this.safeParseExtractionJson(
      docField?.confidenceScoreJson,
    );
    const overrideReasons = this.parseOverrideReasonMap(docField?.overrideReason, docType);

    const getValueCI = (
      source: Record<string, unknown>,
      canonicalKey: string,
    ): unknown => {
      for (const k of Object.keys(source)) {
        if (this.getCanonicalExtractionKey(docType, k) === canonicalKey) {
          return source[k];
        }
      }
      return undefined;
    };

    const docConfig = getFileUploadDocumentConfig(this.normalizeDocType(docType));
    const configFields = docConfig?.extractedFields ?? [];
    const mappedCanonicals = new Set<string>();

    if (!configFields.length) {
      return fields;
    }

    // Prefer configured field order/labels, while matching values by canonical keys.
    for (const configField of configFields) {
      const canonicalKey = this.getCanonicalExtractionKey(docType, String(configField?.key ?? ''));
      if (!canonicalKey || mappedCanonicals.has(canonicalKey) || this.shouldHideExtractionField(canonicalKey)) {
        continue;
      }

      mappedCanonicals.add(canonicalKey);

      const submittedRaw = getValueCI(submitted, canonicalKey);
      const extractedRaw = getValueCI(extracted, canonicalKey);
      const finalRaw = getValueCI(finalVal, canonicalKey) ?? extractedRaw;

      const isDateField = this.isDateField(docType, canonicalKey);

      fields.push({
        key: configField.key,
        label: configField.label || this.toFieldLabel(configField.key),
        submittedValue: this.toDisplayValue(
          isDateField ? DateTimeConversionHelper.formatDateForPayload(submittedRaw) : submittedRaw
        ),
        extractedValue: this.toDisplayValue(
          isDateField ? DateTimeConversionHelper.formatDateForPayload(extractedRaw) : extractedRaw
        ),
        finalValue: this.toDisplayValue(
          isDateField ? DateTimeConversionHelper.formatDateForPayload(finalRaw) : finalRaw
        ),
        overrideReason: this.toDisplayValue(overrideReasons[canonicalKey], ''),
        confidence: getValueCI(confidenceScores, canonicalKey),
        status: this.normalizeStatusValue(getValueCI(fieldStatus, canonicalKey)),
      });
    }

    return fields;
  }

  private reconcileSponsorDocumentsFromApplicant(): void {
    if (!this.isPrimaryApplicant || !this.isSponsorCompany || this.sponsorshipDocuments.length === 0) {
      return;
    }

    const sponsorTypes = new Set(this.sponsorDocTypes);
    const firstSponsor = this.sponsorshipDocuments[0];
    const currentCompanyDocuments = Array.isArray(firstSponsor?.companyDocuments)
      ? firstSponsor.companyDocuments
      : [];

    this.documents
      .filter((document: any) => sponsorTypes.has(this.normalizeDocType(String(document?.documentType ?? ''))))
      .forEach((document: any) => {
        const alreadyPresent = currentCompanyDocuments.some(
          (companyDocument: any) => companyDocument?.documentId === document?.documentId,
        );

        if (!alreadyPresent) {
          currentCompanyDocuments.push({
            ...document,
            documentType: this.normalizeDocType(String(document?.documentType ?? '')),
          });
        }
      });

    firstSponsor.companyDocuments = currentCompanyDocuments;
  }

  private attachExtractedFieldToDocumentCollection(documents: any[], docField: any): void {
    const documentIndex = documents.findIndex(
      (document: any) => document?.documentId === docField?.documentInfoId,
    );

    if (documentIndex === -1) {
      return;
    }

    documents[documentIndex].extractionStatus = docField.extractionStatus;
    documents[documentIndex].extractedFields = this.parseExtractedFields(docField);
  }

  private getCanonicalExtractionKey(docType: string, rawKey: string): string {
    const normalizedDocType = this.normalizeDocType(docType).trim().toLowerCase();
    const normalizedKey = rawKey.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
    const docAliases = EXTRACTION_DOC_TYPE_KEY_ALIASES[normalizedDocType] ?? {};

    return docAliases[normalizedKey]
      ?? EXTRACTION_COMMON_KEY_ALIASES[normalizedKey]
      ?? rawKey;
  }

  private safeParseExtractionJson(raw: unknown): Record<string, unknown> {
    if (typeof raw !== 'string' || !raw.trim()) {
      return {};
    }

    const normalized = raw
      .trim()
      .replace(/\r?\n/g, '\n')
      .replace(/,\s*([}\]])/g, '$1')
      .replace(/:\s*'([^']*)'/g, ': "$1"');

    const attempts = [normalized, this.repairMissingCommas(normalized)];

    for (const candidate of attempts) {
      try {
        const parsed = JSON.parse(candidate);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          return parsed as Record<string, unknown>;
        }
      } catch {
        // continue trying next strategy
      }
    }

    return {};
  }

  private repairMissingCommas(jsonLike: string): string {
    return jsonLike.replace(
      /(":[^,\n{}[\]]+)\s*\n\s*(")/g,
      '$1,\n$2',
    );
  }

  private toDisplayValue(value: unknown, fallback = '-'): string {
    if (value === null || value === undefined) {
      return fallback;
    }

    if (typeof value === 'string') {
      const normalized = value.trim();
      return normalized || fallback;
    }

    if (
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      typeof value === 'bigint'
    ) {
      const normalized = `${value}`.trim();
      return normalized || fallback;
    }

    return fallback;
  }

  private toFieldLabel(key: string): string {
    if (EXTRACTION_FIELD_LABELS[key]) {
      return EXTRACTION_FIELD_LABELS[key];
    }

    const label = key
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replaceAll('_', ' ')
      .trim();
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  private normalizeStatusValue(
    rawStatus: unknown,
  ): 'match' | 'mismatch' | 'corrected' | 'accepted' | undefined {
    if (typeof rawStatus !== 'string') {
      return undefined;
    }
    const normalized = rawStatus.trim().toLowerCase();
    if (
      normalized === 'match' ||
      normalized === 'mismatch' ||
      normalized === 'corrected' ||
      normalized === 'accepted'
    ) {
      return normalized;
    }
    return undefined;
  }

  private resolveRowStatus(
    rowStatus: unknown,
    isEdited: boolean,
    finalValue: string,
    overrideReason: string,
  ): 'match' | 'mismatch' | 'corrected' | 'accepted' | '-' {
    const statusFromApi = this.normalizeStatusValue(rowStatus);

    if (!statusFromApi) {
      return '-';
    }

    if (!isEdited) {
      return statusFromApi;
    }

    const normalizedFinal = finalValue.trim();
    if (!normalizedFinal || normalizedFinal === '-') {
      return 'mismatch';
    }

    if (overrideReason.trim().length > 0) {
      return 'corrected';
    }

    return 'mismatch';
  }

  private normalizeDocType(type: string): string {
    const trimmedType = type.trim();
    if (!trimmedType) {
      return trimmedType;
    }

    const directAlias = DOCUMENT_TYPE_ALIASES[trimmedType];
    if (directAlias) {
      return directAlias;
    }

    const normalizedTypeKey = this.normalizeDocTypeKey(trimmedType);

    for (const [alias, canonical] of Object.entries(DOCUMENT_TYPE_ALIASES)) {
      if (this.normalizeDocTypeKey(alias) === normalizedTypeKey) {
        return canonical;
      }
    }

    for (const canonical of Object.keys(DOCUMENT_LABELS)) {
      if (this.normalizeDocTypeKey(canonical) === normalizedTypeKey) {
        return canonical;
      }
    }

    return trimmedType;
  }

  private normalizeDocTypeKey(value: string): string {
    return value.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  }

  private buildUploadConfig(docType: string): FileUploadModalConfig {
    const docConfig = getFileUploadDocumentConfig(docType);

    return {
      title: docConfig?.label ?? this.getDocLabel(docType),
      docType,
      applicantInfoId: this.applicantId,
      ocrExtractHandler: (payload: FormData) =>
        this.applicationReviewService.ocrExtract(payload),
      isExtractable: docConfig?.isExtractable ?? false,
      existingFilesCount: this.getDocumentsByType(docType).length,
      documentTypeReadonly: true,
      showDocumentTypeDropdown: true,
      showExtractionActions: true,
      isSponsorSelf: this.isLeadSelfSponsored(),
      ocrSubmitHandler: (payload: FormData) =>
        this.applicationReviewService.ocrSubmit(payload),
      onSubmissionSuccess: () => this.fetchDocuments(),
    };
  }

  private buildGlobalUploadConfig(): FileUploadModalConfig {
    return {
      title: 'Upload Documents',
      applicantInfoId: this.applicantId,
      ocrExtractHandler: (payload: FormData) =>
        this.applicationReviewService.ocrExtract(payload),
      ocrSubmitHandler: (payload: FormData) =>
        this.applicationReviewService.ocrSubmit(payload),
      allowedDocTypes: this.availableUploadDocTypes,
      existingFilesCount: 0,
      existingFilesCountByDocType: this.buildGlobalExistingFilesCountByDocType(),
      showReplaceWarningOnExisting: true,
      documentTypeReadonly: false,
      showDocumentTypeDropdown: true,
      showExtractionActions: true,
      isSponsorSelf: this.isLeadSelfSponsored(),
      onSubmissionSuccess: () => this.fetchDocuments(),
    };
  }

  private buildGlobalExistingFilesCountByDocType(): Record<string, number> {
    const existingFilesCountByDocType: Record<string, number> = {};

    this.availableUploadDocTypes.forEach((docType) => {
      const normalizedDocType = this.normalizeDocType(String(docType));
      existingFilesCountByDocType[normalizedDocType] = this.getDocumentsByType(normalizedDocType).length;
    });

    return existingFilesCountByDocType;
  }

  private deleteFile(file: any): Observable<unknown> {
    return this.apiService
      .deleteMafDocument({
        leadInfoId: this.leadInfoId,
        documentInfoId: file.documentId,
        module: 'DocumentSubmission',
        isApplicationReview: true
      })
  }

  private getDownloadFileName(contentDisposition: string | null): string {
    if (!contentDisposition) {
      return 'all-document-zip.zip';
    }

    const utfMatch = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
    if (utfMatch?.[1]) {
      return decodeURIComponent(utfMatch[1]);
    }

    const basicMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
    return basicMatch?.[1] || 'all-document-zip.zip';
  }

  private triggerBrowserDownload(fileBlob: Blob, fileName: string): void {
    const url = globalThis.URL.createObjectURL(fileBlob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    globalThis.URL.revokeObjectURL(url);
  }

  private showToast(message: string, status: 'success' | 'error' | 'warning' = 'success') {
    this.toaster.show({ message, status, timeout: 3000 });
  }

  private getExtractionStatusCode(rawStatus: unknown): ExtractionStatus | null {
    if (typeof rawStatus === 'number') {
      if (
        rawStatus === ExtractionStatus.NotExtracted ||
        rawStatus === ExtractionStatus.Extracted ||
        rawStatus === ExtractionStatus.ExtractionFailed ||
        rawStatus === ExtractionStatus.Overridden
      ) {
        return rawStatus;
      }
      return null;
    }

    const normalized = typeof rawStatus === 'string'
      ? rawStatus.trim().toLowerCase()
      : '';
    if (!normalized) {
      return null;
    }

    const compact = normalized.replace(/[^a-z0-9]/g, '');

    if (
      compact === '0'
      || compact.includes('notextracted')
      || compact.includes('skipextraction')
      || compact.includes('skippedextraction')
      || compact === 'skip'
      || compact === 'skipped'
    ) {
      return ExtractionStatus.NotExtracted;
    }

    if (
      compact === '2'
      || compact.includes('extractionfailed')
      || compact === 'failed'
      || compact.includes('ocrfailed')
    ) {
      return ExtractionStatus.ExtractionFailed;
    }

    if (
      compact === '3'
      || compact.includes('overridden')
      || compact.includes('override')
    ) {
      return ExtractionStatus.Overridden;
    }

    if (
      compact === '1'
      || compact.includes('extracted')
      || compact.includes('success')
    ) {
      return ExtractionStatus.Extracted;
    }

    return null;
  }

  private shouldHideExtractionField(canonicalKey: string): boolean {
    const normalizedKey = canonicalKey.toLowerCase();
    if (normalizedKey === 'sponsorname' && this.isLeadSelfSponsored()) {
      return true;
    }

    return EXTRACTION_HIDDEN_KEYS.some((hiddenKey) => hiddenKey.toLowerCase() === normalizedKey);
  }

  isLeadSelfSponsored(): boolean {
    if (this.isSponsorSelf) {
      return true;
    }

    const sponsorValue = this.sponsorshipDocumentsSnapshot?.[0]?.sponsor
      ?? this.sponsorshipDocuments?.[0]?.sponsor;

    if (typeof sponsorValue === 'number') {
      return sponsorValue === 1;
    }

    if (String(sponsorValue ?? '').trim() === '1') {
      return true;
    }

    const normalizedSponsor = String(sponsorValue ?? '').trim().toLowerCase().replace(/[^a-z]/g, '');
    return normalizedSponsor === 'self' || normalizedSponsor === 'selfsponsored';
  }

  private parseOverrideReasonMap(
    rawOverrideReason: unknown,
    docType: string,
  ): Record<string, string> {
    const result: Record<string, string> = {};

    const applyEntry = (key: string, reason: unknown): void => {
      const normalizedReason = typeof reason === 'string'
        ? reason.trim()
        : '';
      if (!normalizedReason) {
        return;
      }

      const canonicalKey = this.getCanonicalExtractionKey(docType, key);
      result[canonicalKey] = normalizedReason;
    };

    if (!rawOverrideReason) {
      return result;
    }

    if (typeof rawOverrideReason === 'object' && !Array.isArray(rawOverrideReason)) {
      Object.entries(rawOverrideReason as Record<string, unknown>).forEach(([key, value]) => {
        applyEntry(key, value);
      });
      return result;
    }

    const rawText = typeof rawOverrideReason === 'string'
      ? rawOverrideReason.trim()
      : '';
    if (!rawText) {
      return result;
    }

    const parsedJson = this.safeParseExtractionJson(rawText);
    if (Object.keys(parsedJson).length > 0) {
      Object.entries(parsedJson).forEach(([key, value]) => {
        applyEntry(key, value);
      });
      return result;
    }

    const segments = rawText.split(/\/{2,}/g).map((segment) => segment.trim()).filter((segment) => !!segment);
    segments.forEach((segment) => {
      const separatorIndex = segment.indexOf(':');
      if (separatorIndex <= 0) {
        return;
      }

      const key = segment.slice(0, separatorIndex).trim();
      const value = segment.slice(separatorIndex + 1).trim();
      applyEntry(key, value);
    });

    return result;
  }

  private validateMandatoryDocumentsBeforeMarkFinal(): boolean {
    const missingDocs: string[] = [];

    this.mandatoryBaseDocTypes.forEach((docType) => {
      if (this.getDocumentsByType(docType).length === 0) {
        missingDocs.push(this.getDocLabel(docType));
        this.missingMandatoryDocTypes.add(docType);
      }
    });

    if (this.isPrimaryApplicant && this.isSponsorCompany) {
      const sponsorDocs = this.sponsorshipDocuments.flatMap((sponsor: any) => sponsor?.companyDocuments ?? []);
      this.mandatorySponsorDocTypes.forEach((docType) => {
        const uploaded = sponsorDocs.some((doc: any) => this.normalizeDocType(doc?.documentType) === docType);
        if (!uploaded) {
          missingDocs.push(this.getDocLabel(docType));
          this.missingMandatoryDocTypes.add(docType);
        }
      });
    }

    if (missingDocs.length > 0) {
      this.showToast(
        `Upload mandatory document(s): ${missingDocs.join(', ')}.`,
        'warning',
      );
      return false;
    }

    return true;
  }

  private getRequoteMismatchRows(): Array<{
    documentId: string;
    rowKey: string;
    fieldLabel: string;
    submittedValue: string;
    extractedValue: string;
    finalValue: string;
  }> {
    const warningRows: Array<{
      documentId: string;
      rowKey: string;
      fieldLabel: string;
      submittedValue: string;
      extractedValue: string;
      finalValue: string;
    }> = [];
    const requoteKeys = new Set(['DateOfBirth', 'Gender']);

    this.documents
      .filter((doc) => this.isDocExtractable(doc.documentType))
      .forEach((doc) => {
        const rows = this.getFieldRows(doc, doc.documentType);
        rows.forEach((row) => {
          const acceptedKeys = this.acceptedMismatchRowKeysByDocument.get(String(doc.documentId ?? '')) ?? new Set<string>();
          if (acceptedKeys.has(row.key)) {
            return;
          }

          const canonicalKey = this.getCanonicalExtractionKey(doc.documentType, row.key);
          if (!requoteKeys.has(canonicalKey)) {
            return;
          }

          if (row.status !== 'mismatch') {
            return;
          }

          const submittedValue = this.toDisplayValue(row.submittedValue);
          const extractedValue = this.toDisplayValue(row.extractedValue);
          const finalValue = this.toDisplayValue(
            this.hasMeaningfulValue(row.finalValue)
              ? row.finalValue
              : row.extractedValue,
          );

          warningRows.push({
            documentId: String(doc.documentId ?? ''),
            rowKey: row.key,
            fieldLabel: row.label,
            submittedValue,
            extractedValue,
            finalValue,
          });
        });
      });

    return warningRows;
  }

  closeRequoteWarningModal(): void {
    this.showRequoteWarningModal = false;
    this.requoteWarningRows = [];
  }

  reopenRequoteWarningModal(): void {
    if (this.lastRequoteWarningRows.length === 0) {
      return;
    }

    this.requoteWarningRows = [...this.lastRequoteWarningRows];
    this.showRequoteWarningModal = true;
  }

  onIgnoreAndAcceptFromRequoteWarning(): void {
    this.requoteWarningRows.forEach((row) => {
      const keys = this.acceptedMismatchRowKeysByDocument.get(row.documentId) ?? new Set<string>();
      keys.add(row.rowKey);
      this.acceptedMismatchRowKeysByDocument.set(row.documentId, keys);
    });

    this.closeRequoteWarningModal();
    this.executeMarkFinalFlow(true);
  }

  onRequestRequoteFromWarning(): void {
    const reasonLines = this.requoteWarningRows.map(
      (row) => `${row.fieldLabel}: submitted ${row.submittedValue}, final ${row.finalValue}`,
    );
    const prefilledReason = `Potential requote required due to mismatch in premium-impacting fields. ${reasonLines.join(' | ')}`;
    this.closeRequoteWarningModal();
    this.requestRequoteApi.emit(prefilledReason);
  }

  onBackToReviewFromRequoteWarning(): void {
    this.closeRequoteWarningModal();
    this.backToReviewQueueRequested.emit();
  }

  private getAge(): number | null {
    if (!this.applicantDob) return null;

    const dob = new Date(this.applicantDob);
    const today = new Date();

    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();

    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;

    return age;
  }

  private filterMinorDocs(types: string[], includeMinor: boolean): string[] {
    const minorDocs = new Set(MINOR_ONLY_DOCUMENT_TYPES);
    return includeMinor ? types : types.filter((t) => !minorDocs.has(t));
  }

  private getFieldEditState(
    documentId: string | undefined,
    rowKey: string,
    defaultFinalValue = '-',
    defaultOverrideReason = '',
  ): { finalValue: string; overrideReason: string; isEdited: boolean } {
    const safeDocId = documentId || 'unknown-doc';
    let rowMap = this.extractedFieldEdits.get(safeDocId);

    if (!rowMap) {
      rowMap = new Map<
        string,
        { finalValue: string; overrideReason: string; isEdited: boolean }
      >();
      this.extractedFieldEdits.set(safeDocId, rowMap);
    }

    const current = rowMap.get(rowKey);
    if (current) {
      return current;
    }

    const next = {
      finalValue: defaultFinalValue || '-',
      overrideReason: defaultOverrideReason,
      isEdited: false,
    };
    rowMap.set(rowKey, next);
    return next;
  }

  private get isInteractionReadOnly(): boolean {
    return this.isReadOnly || this.isAllMarkedFinal;
  }

  private getExistingFieldEditState(
    documentId: string | undefined,
    rowKey: string,
  ): { finalValue: string; overrideReason: string; isEdited: boolean } | undefined {
    const safeDocId = documentId || 'unknown-doc';
    return this.extractedFieldEdits.get(safeDocId)?.get(rowKey);
  }

  private getOverrideReasonTooltipText(): string {
    return 'Override Reasons';
  }

  private resolveFinalizedState(): boolean {
    return this.isDocumentReviewed === true;
  }

  private hasActionRequiredIssues(): boolean {
    return !this.resolveFinalizedState();
  }

  private hasAllMandatoryDocumentsUploaded(): boolean {
    const hasAllApplicantMandatory = Array.from(this.mandatoryBaseDocTypes).every(
      (docType) => this.getDocumentsByType(docType).length > 0,
    );

    if (!hasAllApplicantMandatory) {
      return false;
    }

    if (!this.isPrimaryApplicant || !this.isSponsorCompany) {
      return true;
    }

    const sponsorDocs = this.sponsorshipDocuments.flatMap((sponsor: any) => sponsor?.companyDocuments ?? []);
    return Array.from(this.mandatorySponsorDocTypes).every((docType) =>
      sponsorDocs.some((doc: any) => this.normalizeDocType(doc?.documentType) === docType),
    );
  }

  private isFinalValueOptional(
    docType: string,
    rowKey: string,
  ): boolean {
    const normalizedDocType = this.normalizeDocType(docType);
    const canonicalKey = this.getCanonicalExtractionKey(
      normalizedDocType,
      rowKey,
    );

    return (
      normalizedDocType === 'Passport' &&
      ['middlename', 'secondname'].includes(
        canonicalKey.toLowerCase(),
      )
    );
  }

  private validateExtractedRowsForSave(): string | null {
    const extractableDocs = this.getValidationDocumentGroups();
    if (extractableDocs.length === 0) {
      return 'Upload and extract at least one document before saving.';
    }

    const docsWithoutRows = extractableDocs.filter((group) => group.flatMap((doc) => this.getDocumentFieldRows(doc, doc.documentType)).length === 0);
    if (docsWithoutRows.length > 0) {
      docsWithoutRows.forEach((group) => group.forEach((doc) => this.expandedRows.add(doc.documentId)));
      return 'Extract uploaded documents and review final values before saving.';
    }

    const docsWithMissingFinalValues = extractableDocs.filter((group) =>
      group.some((doc) =>
        this.getDocumentFieldRows(doc, doc.documentType).some((row) => {
          if (row.status === 'accepted') {
            return false;
          }

          if (this.isFinalValueOptional(doc.documentType, row.key)) {
            return false;
          }

          const finalValue = String(row.finalValue ?? '').trim();
          return !finalValue || finalValue === '-';
        }),
      ),
    );

    if (docsWithMissingFinalValues.length > 0) {
      docsWithMissingFinalValues.forEach((group) => {
        group.forEach((doc) => {
          this.expandedRows.add(doc.documentId);
          const missingFinalValueRowKeys = this.getDocumentFieldRows(doc, doc.documentType)
            .filter((row) => {
              if (row.status === 'accepted') {
                return false;
              }

              if (this.isFinalValueOptional(doc.documentType, row.key)) {
                return false;
              }

              const finalValue = String(row.finalValue ?? '').trim();
              return !finalValue || finalValue === '-';
            })
            .map((row) => row.key);
          if (missingFinalValueRowKeys.length > 0) {
            this.missingFinalValueRows.set(doc.documentId, new Set(missingFinalValueRowKeys));
          }
        });
      });
      return 'Provide final values for all extracted fields before saving.';
    }

    return null;
  }

  private getValidationDocumentGroups(): any[][] {
    const grouped = new Map<string, any[]>();

    this.documents.forEach((doc) => {
      if (!this.isDocExtractable(doc.documentType)) {
        return;
      }

      const normalizedType = this.normalizeDocType(String(doc?.documentType ?? ''));

      // For passport front/back pairs, only the page with actual extraction rows should participate
      // in validation-driven auto-expansion. An empty passport page should never be opened just because
      // the paired file has data to review.
      if (normalizedType === 'Passport' && this.getDocumentFieldRows(doc, doc.documentType).length === 0) {
        return;
      }

      const groupKey = normalizedType === 'Passport' ? 'Passport' : `${normalizedType}:${doc.documentId ?? ''}`;
      const group = grouped.get(groupKey) ?? [];
      group.push(doc);
      grouped.set(groupKey, group);
    });

    return Array.from(grouped.values());
  }

  private persistDocumentChanges(action: 'save' | 'mark-final', isMarkFinal = true): void {
    const payload = this.buildDocumentSavePayload(action === 'mark-final' ? isMarkFinal : null);
    if (!payload) {
      return;
    }

    const request$ = action === 'mark-final'
      ? this.applicationReviewService.markAllDocumentsAsFinal(payload)
      : this.applicationReviewService.saveApplicationReviewDocuments(payload);

    this.applicationReviewService.subscribeWithCleanup(
      request$,
      (response: any) => {
        if (action === 'mark-final') {
          if (!isMarkFinal) {
            this.acceptedMismatchRowKeysByDocument.clear();
          }
        }

        // Force the parent to run a single refresh/loader and success toast once the backend event propagation completes.
        const successMessage = response?.messages?.[0]
          ?? response?.messages
          ?? (
            action === 'mark-final'
              ? (isMarkFinal ? 'All document fields marked as final.' : 'Document fields reopened successfully.')
              : 'Document changes saved successfully.'
          );
        this.reviewStatusRefreshRequested.emit({
          message: successMessage,
          delayMs: action === 'mark-final' && !isMarkFinal ? 0 : 5000,
        });

        this.hasPendingEdits = false;
        this.syncTouchedState(false);
        // Preserve the user's current in-memory edits while the delayed refresh completes.
        // Clearing this map here causes the UI to snap back to the old server values before the latest data loads.
      },
      this.destroyRef,
    );
  }

  private syncTouchedState(touched: boolean): void {
    this.shared.setFormTouchedState(touched);
  }

  private validateRowsBeforeMarkFinal(): string | null {
    const uploadedExtractableDocs = this.documents.filter((doc) =>
      this.isDocExtractable(doc.documentType),
    );

    let hasUnresolvedFinalValue = false;
    let hasUnresolvedMismatch = false;

    for (const file of uploadedExtractableDocs) {
      const rows = this.getFieldRows(file, file.documentType);
      if (rows.length === 0) {
        continue;
      }

      const missingOverrideKeys = new Set<string>();
      const acceptedKeys = this.acceptedMismatchRowKeysByDocument.get(file.documentId) ?? new Set<string>();

      rows.forEach((row) => {
        if (acceptedKeys.has(row.key)) {
          return;
        }

        if (row.status !== 'mismatch') {
          return;
        }

        const finalValue = String(row.finalValue ?? '').trim();

        if (
          (!finalValue || finalValue === '-') &&
          !this.isFinalValueOptional(file.documentType, row.key)
        ) {
          hasUnresolvedFinalValue = true;
          return;
        }


        if (row['isFinalValueEdited'] !== true) {
          hasUnresolvedMismatch = true;
          return;
        }

        const overrideReason = String(row.overrideReason ?? '').trim();
        if (!overrideReason) {
          missingOverrideKeys.add(row.key);
        }
      });

      if (missingOverrideKeys.size > 0) {
        this.missingOverrideReasonRows.set(file.documentId, missingOverrideKeys);
        this.expandedRows.add(file.documentId);
      }
    }

    if (hasUnresolvedMismatch) {
      return 'Mismatches found. Edit the final value to resolve mismatched fields before marking all as final.';
    }

    if (hasUnresolvedFinalValue) {
      return 'Enter final value for all mismatched fields before marking all as final.';
    }

    if (this.missingOverrideReasonRows.size > 0) {
      return 'Select override reason for all corrected fields before marking all as final.';
    }

    return null;
  }

  private validateEditedRowsBeforeSave(): string | null {
    const extractableDocs = this.documents.filter((doc) => this.isDocExtractable(doc.documentType));

    extractableDocs.forEach((file) => {
      const rows = this.getFieldRows(file, file.documentType);
      const missingOverrideKeys = new Set<string>();

      rows.forEach((row) => {
        if (row['isFinalValueEdited'] !== true) {
          return;
        }

        const finalValue = String(row.finalValue ?? '').trim();
        if (!finalValue || finalValue === '-') {
          return;
        }

        const overrideReason = String(row.overrideReason ?? '').trim();
        if (overrideReason) {
          return;
        }

        missingOverrideKeys.add(row.key);
      });

      if (missingOverrideKeys.size > 0) {
        this.missingOverrideReasonRows.set(file.documentId, missingOverrideKeys);
        this.expandedRows.add(file.documentId);
      }
    });

    if (this.missingOverrideReasonRows.size > 0) {
      return 'Select override reason for all edited fields before saving.';
    }

    return null;
  }

  private buildDocumentSavePayload(isMarkFinal: boolean | null): FormData | null {
    const safeLeadInfoId = String(this.leadInfoId ?? '').trim();
    const safeFinalSubmissionId = String(this.mafFinalSubmissionId ?? '').trim();

    if (!safeLeadInfoId || !safeFinalSubmissionId) {
      this.showToast('Unable to save documents. Missing lead or final submission details.', 'error');
      return null;
    }

    const contactInfoId = String(
      this.contactInfoId ||
      this.leadInfo?.contactInfoId ||
      this.basicInfo?.contactInfoId ||
      (this.basicInfo as any)?.leadInfo?.contactInfoId ||
      (this.basicInfo as any)?.contactInfoNew?.id ||
      ''
    ).trim();

    const formData = new FormData();
    formData.append('ApplicantDocuments.LeadInfoId', safeLeadInfoId);
    formData.append('ApplicantDocuments.ContactInfoId', contactInfoId);
    formData.append('ApplicantDocuments.FinalSubmissionId', safeFinalSubmissionId);
    formData.append('ApplicantDocuments.Module', 'DocumentSubmission');

    const applicantInfoId = String(this.applicantId ?? '').trim();
    const reviewQueueId = String(this.reviewQueueId ?? '').trim();
    const includeAllApplicants = isMarkFinal === null;
    const selectedApplicant = includeAllApplicants ? null : this.getSelectedApplicantSnapshot();
    const resolvedMafFormSubmissionId = this.resolveMafFormSubmissionIdForApplicant(applicantInfoId);

    formData.append('IsMarkFinal', String(isMarkFinal ?? false));
    formData.append('ApplicantInfoId', applicantInfoId);
    formData.append('ReviewQueueId', reviewQueueId);
    formData.append('MafFormSubmissionId', resolvedMafFormSubmissionId);

    const selectedApplicantDocuments = selectedApplicant && Array.isArray(selectedApplicant['documents'])
      ? selectedApplicant['documents'] as Array<Record<string, unknown>>
      : [];
    const allowedExtractedFieldDocumentIds = selectedApplicant
      ? new Set<string>(
        selectedApplicantDocuments
          .map((document) =>
            typeof document['documentId'] === 'string'
              ? document['documentId'].trim()
              : '',
          )
          .filter((documentId: string) => !!documentId),
      )
      : null;

    this.appendMafFormSubmissions(formData);
    this.appendApplicants(formData, selectedApplicant ? [selectedApplicant] : null);
    this.appendDocumentExtractedFields(formData, allowedExtractedFieldDocumentIds);

    return formData;
  }

  private resolveMafFormSubmissionIdForApplicant(applicantId: string | null | undefined): string {
    const targetId = String(applicantId ?? '').trim();
    if (!targetId) {
      return '';
    }

    const submissions = Array.isArray(this.mafSubmissions) && this.mafSubmissions.length > 0
      ? this.mafSubmissions
      : (this.mafFormSubmissionsSnapshot?.length ? this.mafFormSubmissionsSnapshot : []);

    for (const sub of submissions) {
      const applicants = Array.isArray(sub?.applicants) ? sub.applicants : [];
      const matched = applicants.some(
        (a: any) => String(a?.applicantId ?? a?.id ?? '').trim() === targetId,
      );
      if (matched && sub?.mafFormSubmissionId) {
        return String(sub.mafFormSubmissionId).trim();
      }
    }

    const applicant = this.applicantDocumentsSnapshot.find(
      (a: any) => String(a?.applicantId ?? '').trim() === targetId,
    );
    if (applicant?.['mafFormSubmissionId']) {
      return String(applicant['mafFormSubmissionId']).trim();
    }

    return '';
  }

  private getSelectedApplicantSnapshot(): Record<string, unknown> | null {
    const selectedApplicantId = String(this.applicantId ?? '').trim();
    if (!selectedApplicantId) {
      return null;
    }

    return this.applicantDocumentsSnapshot.find(
      (applicant: Record<string, unknown>) => {
        const applicantId = applicant['applicantId'];
        return typeof applicantId === 'string' && applicantId.trim() === selectedApplicantId;
      },
    ) ?? null;
  }

  private appendMafFormSubmissions(formData: FormData): void {
    const submissions = Array.isArray(this.mafSubmissions) && this.mafSubmissions.length > 0
      ? this.mafSubmissions
      : this.mafFormSubmissionsSnapshot;

    submissions.forEach((submission, submissionIndex) => {
      formData.append(
        `ApplicantDocuments.MafFormSubmissions[${submissionIndex}].mafFormSubmissionId`,
        String(submission?.mafFormSubmissionId ?? ''),
      );
      formData.append(
        `ApplicantDocuments.MafFormSubmissions[${submissionIndex}].sharedMafFormSubmissionId`,
        String(submission?.sharedMafFormSubmissionId ?? ''),
      );

      (submission?.files ?? submission?.documents ?? []).forEach((file: any, fileIndex: number) => {
        const filePath = file?.formFiles ?? file?.filePath ?? file?.viewUrl ?? '';
        const docId = file?.documentInfoId ?? file?.documentId ?? '';
        formData.append(
          `ApplicantDocuments.MafFormSubmissions[${submissionIndex}].files[${fileIndex}].formFiles`,
          String(filePath),
        );
        formData.append(
          `ApplicantDocuments.MafFormSubmissions[${submissionIndex}].files[${fileIndex}].documentInfoId`,
          String(docId),
        );
      });
    });
  }

  private appendApplicants(
    formData: FormData,
    applicantsOverride: Array<Record<string, unknown>> | null = null,
  ): void {
    const applicants = applicantsOverride ?? this.applicantDocumentsSnapshot;

    applicants.forEach((applicant, applicantIndex) => {
      const appApplicantId = String(applicant?.applicantId ?? '');
      const matchedApplicant = this.applicants?.find(
        (a: any) => String(a?.id ?? a?.applicantId ?? '').trim() === appApplicantId.trim(),
      );
      const relationshipToSelf = String(
        applicant?.relationshipToSelf ?? matchedApplicant?.relationshipToSelf ?? '',
      );
      const appMafFormSubId = this.resolveMafFormSubmissionIdForApplicant(appApplicantId);

      formData.append(
        `ApplicantDocuments.Applicant[${applicantIndex}].applicantId`,
        appApplicantId,
      );
      formData.append(
        `ApplicantDocuments.Applicant[${applicantIndex}].relationshipToSelf`,
        relationshipToSelf,
      );
      formData.append(
        `ApplicantDocuments.Applicant[${applicantIndex}].mafFormSubmissionId`,
        appMafFormSubId,
      );
      formData.append(
        `ApplicantDocuments.Applicant[${applicantIndex}].isPrimaryApplicant`,
        String(applicant?.['isPrimaryApplicant'] ?? (applicantIndex === 0)),
      );

      const documents = applicant?.documents ?? [];
      documents.forEach((document: any, documentIndex: number) => {
        const prefix = `ApplicantDocuments.Applicant[${applicantIndex}].documents[${documentIndex}]`;
        formData.append(`${prefix}.documentId`, String(document?.documentId ?? ''));
        formData.append(`${prefix}.documentType`, String(document?.documentType ?? ''));
        formData.append(`${prefix}.documentLabel`, String(document?.documentLabel ?? ''));
        const fileValue =
          document?.file ??
          document?.filepath ??
          document?.viewFilePath ??
          document?.downloadFilePath ??
          '';
        formData.append(`${prefix}.file`, String(fileValue));
      });
    });
  }

  private appendDocumentExtractedFields(
    formData: FormData,
    allowedDocumentIds: Set<string> | null = null,
  ): void {
    const extractedFields = allowedDocumentIds
      ? this.documentExtractedFieldsSnapshot.filter((field) =>
        allowedDocumentIds.has(String(field?.documentInfoId ?? '').trim()),
      )
      : this.documentExtractedFieldsSnapshot;

    extractedFields.forEach((field, fieldIndex) => {
      const updatedField = this.getUpdatedExtractedField(field);
      const prefix = `DocumentExtractedFields[${fieldIndex}]`;

      formData.append(`${prefix}.documentInfoId`, updatedField.documentInfoId);
      formData.append(`${prefix}.submittedValueJson`, updatedField.submittedValueJson);
      formData.append(`${prefix}.extractedValueJson`, updatedField.extractedValueJson);
      formData.append(`${prefix}.finalValueJson`, updatedField.finalValueJson);
      formData.append(`${prefix}.fieldStatusJson`, updatedField.fieldStatusJson);
      formData.append(`${prefix}.confidenceScoreJson`, updatedField.confidenceScoreJson);
      formData.append(`${prefix}.overrideReason`, updatedField.overrideReason);
      formData.append(`${prefix}.extractionStatus`, updatedField.extractionStatus);
    });
  }

  private getUpdatedExtractedField(field: any): {
    documentInfoId: string;
    submittedValueJson: string;
    extractedValueJson: string;
    finalValueJson: string;
    fieldStatusJson: string;
    confidenceScoreJson: string;
    overrideReason: string;
    extractionStatus: string;
  } {
    const documentInfoId = String(field?.documentInfoId ?? '');
    const docType = String(field?.documentType ?? '');
    const submittedValues = this.normalizeExtractionRecordKeys(
      this.safeParseExtractionJson(field?.submittedValueJson),
      docType,
    );
    const extractedValues = this.normalizeExtractionRecordKeys(
      this.safeParseExtractionJson(field?.extractedValueJson),
      docType,
    );
    const finalValues = this.normalizeExtractionRecordKeys(
      this.safeParseExtractionJson(field?.finalValueJson),
      docType,
    );
    const fieldStatuses = this.normalizeExtractionRecordKeys(
      this.safeParseExtractionJson(field?.fieldStatusJson),
      docType,
    );
    const confidenceScores = this.normalizeExtractionRecordKeys(
      this.safeParseExtractionJson(field?.confidenceScoreJson),
      docType,
    );
    const existingOverrideReasons = this.parseOverrideReasonMap(
      field?.overrideReason,
      docType,
    );
    const acceptedRowKeys = this.acceptedMismatchRowKeysByDocument.get(documentInfoId) ?? new Set<string>();
    const editRows = this.extractedFieldEdits.get(documentInfoId);
    const hasEdits = !!editRows?.size || acceptedRowKeys.size > 0;
    const hasExtractedValues = this.hasMeaningfulRecordValues(extractedValues);
    const extractionStatusCode = this.resolveUpdatedExtractionStatusCode(
      field?.extractionStatus,
      hasEdits,
      hasExtractedValues,
    );
    const shouldSendConfidenceScore =
      extractionStatusCode === ExtractionStatus.Extracted
      || extractionStatusCode === ExtractionStatus.Overridden;
    const normalizedConfidenceScoreJson = shouldSendConfidenceScore
      ? JSON.stringify(confidenceScores)
      : '';

    if (hasEdits) {
      const nextFinalValues: Record<string, string> = {
        ...(finalValues as Record<string, string>),
      };
      const nextFieldStatuses: Record<string, string> = {
        ...(fieldStatuses as Record<string, string>),
      };
      const overrideReasons: Record<string, string> = {
        ...existingOverrideReasons,
      };

      editRows?.forEach((edit, key) => {
        const canonicalKey = this.getCanonicalExtractionKey(docType, key);
        const finalValue = this.toDisplayValue(edit.finalValue);
        const overrideReason = String(edit.overrideReason ?? '').trim();

        nextFinalValues[canonicalKey] = finalValue;
        nextFieldStatuses[canonicalKey] = this.resolveRowStatus(
          fieldStatuses[canonicalKey],
          edit.isEdited,
          finalValue,
          overrideReason,
        );

        if (overrideReason) {
          overrideReasons[canonicalKey] = overrideReason;
        } else {
          delete overrideReasons[canonicalKey];
        }
      });

      acceptedRowKeys.forEach((rowKey) => {
        const canonicalKey = this.getCanonicalExtractionKey(docType, rowKey);
        nextFieldStatuses[canonicalKey] = 'accepted';
      });

      return {
        documentInfoId,
        submittedValueJson: JSON.stringify(submittedValues),
        extractedValueJson: JSON.stringify(extractedValues),
        finalValueJson: JSON.stringify(nextFinalValues),
        fieldStatusJson: JSON.stringify(nextFieldStatuses),
        confidenceScoreJson: normalizedConfidenceScoreJson,
        overrideReason: Object.keys(overrideReasons).length > 0
          ? JSON.stringify(overrideReasons)
          : '',
        extractionStatus: this.toExtractionStatusLabel(extractionStatusCode),
      };
    }

    return {
      documentInfoId,
      submittedValueJson: String(field?.submittedValueJson ?? '{}'),
      extractedValueJson: String(field?.extractedValueJson ?? '{}'),
      finalValueJson: String(field?.finalValueJson ?? '{}'),
      fieldStatusJson: String(field?.fieldStatusJson ?? '{}'),
      confidenceScoreJson: shouldSendConfidenceScore
        ? String(field?.confidenceScoreJson ?? '{}')
        : '',
      overrideReason: String(field?.overrideReason ?? ''),
      extractionStatus: this.toExtractionStatusLabel(extractionStatusCode),
    };
  }

  private resolveUpdatedExtractionStatusCode(
    rawStatus: unknown,
    hasEdits: boolean,
    hasExtractedValues: boolean,
  ): ExtractionStatus {
    const statusCode = this.getExtractionStatusCode(rawStatus);

    if (statusCode === ExtractionStatus.NotExtracted) {
      return ExtractionStatus.NotExtracted;
    }

    if (statusCode === ExtractionStatus.ExtractionFailed) {
      return ExtractionStatus.ExtractionFailed;
    }

    if (statusCode === ExtractionStatus.Overridden) {
      return hasEdits ? ExtractionStatus.Overridden : ExtractionStatus.Extracted;
    }

    if (statusCode === ExtractionStatus.Extracted) {
      return hasEdits ? ExtractionStatus.Overridden : ExtractionStatus.Extracted;
    }

    if (!hasExtractedValues) {
      return ExtractionStatus.NotExtracted;
    }

    if (hasEdits) {
      return ExtractionStatus.Overridden;
    }

    return ExtractionStatus.Extracted;
  }

  private hasMeaningfulRecordValues(source: Record<string, unknown>): boolean {
    return Object.values(source).some((value) => this.hasMeaningfulValue(value));
  }

  private toExtractionStatusLabel(status: ExtractionStatus): string {
    if (status === ExtractionStatus.NotExtracted) {
      return 'NotExtracted';
    }

    if (status === ExtractionStatus.ExtractionFailed) {
      return 'ExtractionFailed';
    }

    if (status === ExtractionStatus.Overridden) {
      return 'Overridden';
    }

    return 'Extracted';
  }

  private normalizeExtractionRecordKeys(
    source: Record<string, unknown>,
    docType: string,
  ): Record<string, unknown> {
    const normalized: Record<string, unknown> = {};

    for (const key of Object.keys(source)) {
      const canonicalKey = this.getCanonicalExtractionKey(docType, key);
      if (!(canonicalKey in normalized)) {
        normalized[canonicalKey] = source[key];
      }
    }

    return normalized;
  }

  private normalizeConfidence(raw: unknown): number | string {
    const value = this.parseConfidenceValue(raw);
    if (!Number.isFinite(value)) {
      return '-';
    }

    if (value >= 0 && value <= 1) {
      return Math.round(value * 100);
    }

    if (value > 100) {
      return 100;
    }

    if (value < 0) {
      return 0;
    }

    return Math.round(value);
  }

  private parseConfidenceValue(raw: unknown): number {
    if (typeof raw === 'number') {
      return raw;
    }

    if (typeof raw !== 'string') {
      return Number.NaN;
    }

    const normalized = raw.trim().replace('%', '');
    if (!normalized) {
      return Number.NaN;
    }

    return Number(normalized);
  }

  private hasMeaningfulValue(value: unknown): boolean {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value);
    }

    if (typeof value === 'boolean') {
      return true;
    }

    if (typeof value !== 'string') {
      return false;
    }

    const normalized = value.trim().toLowerCase();
    return normalized !== '' && normalized !== '-' && normalized !== 'null';
  }

  private triggerDocumentFlashFromCurrentState(file: any): void {
    if (!file?.documentId) {
      return;
    }

    const rows = this.getFieldRows(file, file?.documentType || '');
    const hasCorrected = rows.some((row) => row.status === 'corrected');
    if (hasCorrected) {
      this.triggerDocumentFlash(file, 'corrected');
      return;
    }

    const bannerType = this.getDocumentBannerType(file, file?.documentType || '');
    if (bannerType === 'match') {
      this.triggerDocumentFlash(file, 'match');
      return;
    }

    if (bannerType === 'mismatch' || bannerType === 'failed') {
      this.triggerDocumentFlash(file, 'mismatch');
    }
  }

  private triggerDocumentFlash(
    file: any,
    flashType: 'match' | 'mismatch' | 'corrected' | 'deleted',
  ): void {
    if (!file?.documentId) {
      return;
    }

    this.docFlashStates.set(file.documentId, flashType);

    const existingTimer = this.docFlashTimers.get(file.documentId);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    const clearTimer = setTimeout(() => {
      this.docFlashStates.delete(file.documentId);
      this.docFlashTimers.delete(file.documentId);
    }, 1700);

    this.docFlashTimers.set(file.documentId, clearTimer);
  }

}

